import React, { useEffect, useState } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import {
  useBusinessUnitAdminControllerCreatePartnerMutation,
  useBusinessUnitAdminControllerUpdatePartnerMutation,
} from 'src/store/leela-api';

interface PartnerFormModalProps {
  businessUnitId: string;
  partner?: { id: string; name: string; contractExpiry?: string };
  open: boolean;
  onClose: () => void;
}

export const PartnerFormModal: React.FC<PartnerFormModalProps> = ({ businessUnitId, partner, open, onClose }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [createPartner, { isLoading: isCreating }] = useBusinessUnitAdminControllerCreatePartnerMutation();
  const [updatePartner, { isLoading: isUpdating }] = useBusinessUnitAdminControllerUpdatePartnerMutation();
  const isEditing = !!partner;
  const isSubmitting = isCreating || isUpdating;

  const [name, setName] = useState('');
  const [contractExpiry, setContractExpiry] = useState('');

  useEffect(() => {
    if (partner) {
      setName(partner.name);
      setContractExpiry(partner.contractExpiry ?? '');
    } else {
      setName('');
      setContractExpiry('');
    }
  }, [partner, open]);

  const handleContractExpiryChange = (value: string) => {
    if (!value && partner?.contractExpiry) return;
    setContractExpiry(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      if (isEditing && partner) {
        await updatePartner({
          id: partner.id,
          updatePartnerDto: {
            name: name.trim(),
            ...(contractExpiry && { contractExpiry }),
          },
        }).unwrap();
        createToast({ type: AlertType.SUCCESS, message: t('partner_form_update_success') });
      } else {
        await createPartner({
          id: businessUnitId,
          createPartnerDto: {
            name: name.trim(),
            ...(contractExpiry && { contractExpiry }),
          },
        }).unwrap();
        createToast({ type: AlertType.SUCCESS, message: t('partner_form_create_success') });
      }
      onClose();
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data?.message;
      createToast({ type: AlertType.ERROR, message: apiMessage ?? t('partner_form_save_error') });
    }
  };

  return (
    <Modal
      data-testid="partner-form-modal"
      open={open}
      scrollContainer="modal"
      title={isEditing ? t('partner_form_edit_title') : t('partner_form_create_title')}
      width={500}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit}>
        <Column spacing="400">
          <Column spacing="100">
            <Text type="b300">{t('partner_form_name_label')}</Text>
            <Input
              data-testid="partner-form-name"
              placeholder={t('partner_form_name_placeholder')}
              value={name}
              onChange={(value: string) => { setName(value); }}
            />
          </Column>

          <Column spacing="100">
            <Text type="b300">{t('partner_form_contract_expiry_label')}</Text>
            <Text color="secondary" type="b200">{t('partner_form_contract_expiry_help')}</Text>
            <Input
              data-testid="partner-form-expiry"
              placeholder={t('partner_form_date_placeholder')}
              type="date"
              value={contractExpiry}
              onChange={handleContractExpiryChange}
            />
          </Column>
        </Column>

        <div style={{ marginTop: '24px' }}>
          <ModalFooter>
            <Row alignmentHorizontal="end" spacing="300">
              <Button data-testid="partner-form-cancel" type="secondary" onClick={onClose}>
                {t('partner_form_cancel')}
              </Button>
              <Button
                data-testid="partner-form-save"
                disabled={isSubmitting || !name.trim()}
                submit
                type="primary"
              >
                {isEditing ? t('partner_form_save') : t('partner_form_create')}
              </Button>
            </Row>
          </ModalFooter>
        </div>
      </form>
    </Modal>
  );
};
